import { useState } from "react";
import { env } from "../lib/env";
import CopyButton from "../components/CopyButton";
import ChatIcon from "../icons/ChatIcon";
import styles from "./HelpPage.module.css";

interface Props {
  onBack: () => void;
  backLabel?: string;
  onContribute: () => void;
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      className={[styles.chevron, expanded && styles.chevronOpen].filter(Boolean).join(" ")}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function Section({ title, defaultOpen, children }: { title: string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <div className={styles.section}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={[styles.sectionToggle, open && styles.sectionToggleOpen].filter(Boolean).join(" ")}
      >
        <span className={styles.sectionTitle}>{title}</span>
        <ChevronIcon expanded={open} />
      </button>
      {open && (
        <div className={styles.sectionContent}>
          {children}
        </div>
      )}
    </div>
  );
}

export default function HelpPage({ onBack, backLabel, onContribute }: Props) {
  const marketplaceName = env.REACT_APP_MARKETPLACE_NAME;
  // CLI commands (Claude Code) still register the marketplace by URL.
  // The Chat & Cowork desktop client gets it pre-installed via enterprise admin
  // tooling, so there's no GUI "add marketplace" URL to display.
  const catalogUrl = env.REACT_APP_CATALOG_GIT_URL || "";

  return (
    <div>
      <button type="button" onClick={onBack} className={styles.backLink}>
        &larr; {backLabel ?? "Back to Marketplace"}
      </button>

      <h1 className={styles.title}>Help &amp; Setup</h1>
      <p className={styles.intro}>
        The CAI marketplace is already installed in Claude (Chat &amp; Cowork) — find plug-ins in
        Organization Plugins. Claude Code registers it via CLI.
        The same marketplace serves every surface.
      </p>

      <p className={styles.supportBanner}>
        <ChatIcon className={styles.supportBannerIcon} />
        <span>
          Questions or need a hand? Join our{" "}
          <a
            href="https://coxauto.slack.com/archives/C0B4Y1HJXS8"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
          >
            support Slack channel
          </a>
          .
        </span>
      </p>

      <div className={styles.sections}>

        <Section title="Claude (Chat &amp; Cowork) — start here" defaultOpen>
          <p className={styles.pLead}>
            The CAI marketplace is pre-installed for everyone via Claude&rsquo;s enterprise admin tooling —
            there&rsquo;s no marketplace to add. Plug-ins appear directly in Organization Plugins.
          </p>

          <h3 className={styles.subhead}>Install a plug-in</h3>
          <ol className={styles.list}>
            <li>In Claude, go to <strong>Customize → Organization Plugins → + Icon</strong></li>
            <li>Search for the plug-in and click <strong>+</strong> to install it — it&rsquo;s available in your next conversation</li>
          </ol>

          <h3 className={styles.subhead}>Install an individual skill</h3>
          <p className={styles.pFlush}>
            Individual skills unpacked from a plug-in aren&rsquo;t listed in Organization Plugins — the only
            way to use one in Chat or Cowork is to download it and add it manually. (You can also download
            any plug-in this way as a fallback.)
          </p>
          <ol className={styles.list}>
            <li>On a detail page, click <strong>Download</strong> to save the file</li>
            <li><strong>Plug-in:</strong> in Claude, go to <strong>Customize → Upload Plugin</strong> and select the .plugin file</li>
            <li><strong>Skill:</strong> go to <strong>Customize → Skills → Create skill → Upload a skill</strong> and select the skill.md file</li>
          </ol>

          <h3 className={styles.subhead}>Good to know</h3>
          <p className={styles.pFlush}>
            Claude runs both skills and plug-ins. A plug-in&rsquo;s skills, slash commands, and MCP
            connectors all work in Chat — only hooks and sub-agents are Cowork-only, so a plug-in that
            ships those runs fully in Cowork.
          </p>
          <p className={styles.calloutWarning}>
            <strong>Installing is separate from runtime.</strong> A plug-in&rsquo;s bundled MCP connector routes
            through Anthropic&rsquo;s cloud in Chat and Cowork, so a connector that targets an internal-only
            server won&rsquo;t reach it there — even though the plug-in installs fine. Such connectors work in
            Claude Code, which runs locally.
          </p>

          <h3 className={styles.subhead}>Links</h3>
          <ul className={styles.list}>
            <li><a href="https://coxautoinc.sharepoint.com/sites/service-engineering-enablement/delivery-platform/SitePages/Installing-Plugins-and-Skills.aspx" target="_blank" rel="noopener noreferrer" className={styles.link} aria-label="Video: Installing plugins and skills in Claude Desktop (opens in new window)">Video: Installing plugins and skills in Claude Desktop</a></li>
            <li><a href="https://support.claude.com/en/articles/13837440-use-plugins-in-claude" target="_blank" rel="noopener noreferrer" className={styles.link}>Use plugins in Claude</a></li>
            <li><a href="https://claude.com/plugins" target="_blank" rel="noopener noreferrer" className={styles.link}>Browse plugins on claude.com</a></li>
          </ul>
        </Section>

        <Section title="Claude Code (CLI)">
          <h3 className={styles.subheadFirst}>One-time setup: add the marketplace</h3>
          <p>Register this marketplace so Claude Code knows where to find plug-ins:</p>
          <CopyButton text={`claude plugin marketplace add ${catalogUrl}`} />

          <h3 className={styles.subhead}>Installing a plug-in</h3>
          <p>Copy the install command from any plug-in's detail page, or run:</p>
          <p><strong>From any terminal</strong> (outside a Claude Code session):</p>
          <CopyButton text={`claude plugin install <name>@${marketplaceName}`} />
          <p>The plug-in will be available next time you start Claude Code.</p>

          <p className={styles.pSpaced}><strong>Inside a Claude Code session:</strong></p>
          <CopyButton text={`/plugin install <name>@${marketplaceName}`} />
          <p>
            Then run <code className={styles.inlineCode}>/reload-plugins</code> to
            activate immediately without restarting.
          </p>

          <h3 className={styles.subhead}>Links</h3>
          <ul className={styles.list}>
            <li><a href="https://code.claude.com/docs/en/discover-plugins" target="_blank" rel="noopener noreferrer" className={styles.link}>Discover and install plugins</a></li>
            <li><a href="https://code.claude.com/docs/en/plugins" target="_blank" rel="noopener noreferrer" className={styles.link}>Create plugins</a></li>
            <li><a href="https://code.claude.com/docs/en/cli-reference" target="_blank" rel="noopener noreferrer" className={styles.link}>CLI reference</a></li>
          </ul>
        </Section>

        <Section title="Contributing Plug-Ins and Skills">
          <p className={styles.pLead}>
            Publishing is automatic. Any push to a <strong>public or internal</strong> (non-private)
            org-owned GitHub repo that contains a <code className={styles.inlineCode}>.claude-plugin/plugin.json</code> manifest
            is ingested by the marketplace via webhook — no extra step. Every later push keeps the
            catalog entry up to date, and removing the manifest deactivates it.
          </p>

          <h3 className={styles.subhead}>Two ways to publish</h3>
          <ol className={styles.list}>
            <li><strong>From a repo:</strong> Add a <code className={styles.inlineCode}>.claude-plugin/plugin.json</code> to any public or internal org repo — the marketplace catalogs it on the next push.</li>
            <li>
              <strong>Web form — no repo needed:</strong> Publishing a single standalone skill?{" "}
              <button
                type="button"
                onClick={onContribute}
                className={styles.linkInline}
              >
                Contribute a skill
              </button>{" "}
              to upload its files directly — no repository required.
            </li>
          </ol>

          <h3 className={styles.subhead}>Links</h3>
          <ul className={styles.list}>
            <li><a href="https://code.claude.com/docs/en/plugins" target="_blank" rel="noopener noreferrer" className={styles.link}>Plugin authoring guide</a></li>
          </ul>
        </Section>

      </div>
    </div>
  );
}
